import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { bookingStatusToBadge, formatStatusLabel } from '@/shared/utils/statusBadge';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { formatBookingTime } from '@/shared/utils/bookingWhen';
import {
  ATTENTION_LABELS,
  formatDate,
  formatTravelDate,
  shortBookingId,
} from '@/features/admin-bookings/constants';
import type { AdminBookingRow } from '@/types';

const headSx = { fontWeight: 700, whiteSpace: 'nowrap' } as const;

export function AdminBookingsTable({
  rows,
  onSelect,
}: {
  rows: AdminBookingRow[];
  onSelect: (id: string) => void;
}) {
  return (
    <TableContainer>
      <Table size="small" sx={{ minWidth: 980 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Booking</TableCell>
            <TableCell sx={headSx}>Travel date</TableCell>
            <TableCell sx={headSx}>Traveler</TableCell>
            <TableCell sx={headSx}>Guide</TableCell>
            <TableCell sx={headSx}>Destination</TableCell>
            <TableCell sx={headSx} align="right">
              Amount
            </TableCell>
            <TableCell sx={headSx}>Status</TableCell>
            <TableCell sx={headSx}>Payment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                No bookings match these filters
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => onSelect(row.id)}
                sx={{ cursor: 'pointer' }}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onSelect(row.id);
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>
                    {shortBookingId(row.id)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(row.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatTravelDate(row.travelDate)}
                  {formatBookingTime(row) && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatBookingTime(row)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {row.traveler.name ?? '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.traveler.phone ?? row.traveler.email ?? ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.guide.fullName ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.destinationLabel ?? '—'}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {formatGuidePrice(row.grossAmount, row.currency)}
                  </Typography>
                  {row.cancelRefundAmount != null && row.cancelRefundAmount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Refund {formatGuidePrice(row.cancelRefundAmount, row.currency)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                    <StatusBadge
                      status={bookingStatusToBadge(row.status)}
                      label={formatStatusLabel(row.status)}
                    />
                    {row.attentionReasons.map((reason) => (
                      <Chip
                        key={reason}
                        label={ATTENTION_LABELS[reason]}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.payment ? formatStatusLabel(row.payment.status) : 'None'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
